/**
 * Collection PLP infinite pagination: appends <li> rows into staging #product-grid.
 * Fetch uses ?section_id=... (same as facets.js) so markup always includes #ProductGridContainer.
 * skipp-collection-delivery-split MutationObserver then moves items to express/next lists.
 */
class SkippCollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this._busy = false;
    this.observer = null;
    this.maxPages = 1;
    this.nextPageNum = 2;
    this.gridContainer = null;
    this.gridSelector = 'ul#product-grid';
  }

  connectedCallback() {
    if (this.dataset.skippInfiniteInit === 'true') return;

    const maxPages = Math.max(1, parseInt(this.dataset.pageSize || '1', 10) || 1);
    const startPage = Math.max(1, parseInt(this.dataset.currentPage || '1', 10) || 1);
    this.maxPages = maxPages;

    const nextHref = (this.dataset.next || '').trim();
    if (maxPages <= 1 || !nextHref || startPage >= maxPages) {
      this.dismissLoader();
      return;
    }

    this.nextPageNum = startPage + 1;

    this.initGrid();
    if (!this.gridContainer) {
      console.warn('[skipp-collection-infinite-scroll] No staging #product-grid inside #ProductGridContainer');
      this.dismissLoader();
      return;
    }

    this.dataset.skippInfiniteInit = 'true';

    this.observer = new IntersectionObserver(this.reveal.bind(this), {
      root: null,
      rootMargin: '320px',
      threshold: 0,
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    try {
      this.observer?.disconnect();
    } catch (_e) {}
    this.observer = null;
    delete this.dataset.skippInfiniteInit;
  }

  dismissLoader() {
    this.classList.add('skipp-inf-scroll--done');
  }

  initGrid() {
    const scope = document.getElementById('ProductGridContainer');
    if (scope) {
      const ul =
        scope.querySelector('ul#product-grid.skipp-collection-grid--staging') ||
        scope.querySelector('ul#product-grid');
      if (ul) {
        this.gridContainer = ul;
        this.gridSelector = ul.classList?.contains('skipp-collection-grid--staging')
          ? 'ul#product-grid.skipp-collection-grid--staging'
          : 'ul#product-grid';
        return;
      }
    }

    const selectors = [
      'ul#product-grid.skipp-collection-grid--staging',
      'ul#product-grid',
      '#product-grid',
      '.ProductListWrapper',
      '.product-list',
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el && el.nodeName === 'UL') {
        this.gridSelector = selector;
        this.gridContainer = el;
        return;
      }
      if (el) {
        this.gridSelector = selector;
        this.gridContainer = el;
        return;
      }
    }
  }

  reveal(entries) {
    const [entry] = entries || [];
    if (!entry?.isIntersecting || this._busy) return;

    const maxPages = this.maxPages;
    if (!(this.dataset.next || '').trim()) return;

    if (this.nextPageNum > maxPages) {
      this.finish();
      return;
    }

    this._busy = true;
    this.classList.add('skipp-inf-scroll--busy');

    const pageToLoad = this.nextPageNum;
    this.fetchAppend(pageToLoad)
      .then((ok) => {
        if (!ok) return;
        this.nextPageNum = pageToLoad + 1;
        if (pageToLoad >= maxPages) this.finish();
      })
      .finally(() => {
        this._busy = false;
        this.classList.remove('skipp-inf-scroll--busy');
      });
  }

  /** Match facets.js: window URL + paginate.next query + section_id + page */
  buildFetchUrl(pageNum) {
    const next = (this.dataset.next || '').trim();
    const sid = (this.dataset.sectionId || '').trim();

    if (sid) {
      const u = new URL(window.location.href);
      const seed = new URL(next, window.location.origin);
      seed.searchParams.forEach((v, k) => {
        if (k === 'section_id') return;
        u.searchParams.set(k, v);
      });
      u.searchParams.set('page', String(pageNum));
      u.searchParams.set('section_id', sid);
      return u.href;
    }

    const u = new URL(next, window.location.origin);
    u.searchParams.set('page', String(pageNum));
    const liveView = new URL(window.location.href).searchParams.get('view');
    if (liveView && !u.searchParams.get('view')) {
      u.searchParams.set('view', liveView);
    }
    return u.href;
  }

  fetchAppend(pageNum) {
    if (!this.gridContainer) return Promise.resolve(false);

    let fetchUrl;
    try {
      fetchUrl = this.buildFetchUrl(pageNum);
      if (!fetchUrl) return Promise.resolve(false);
    } catch (_e2) {
      return Promise.resolve(false);
    }

    const pickRemoteGrid = (doc) => {
      const remotePg = doc.getElementById('ProductGridContainer');
      if (remotePg) {
        const scoped =
          remotePg.querySelector('ul#product-grid.skipp-collection-grid--staging') ||
          remotePg.querySelector('ul#product-grid');
        if (scoped) return scoped;
      }
      const selector = this.gridSelector || 'ul#product-grid';
      return (
        doc.querySelector(selector) ||
        doc.querySelector('ul#product-grid.skipp-collection-grid--staging') ||
        doc.querySelector('ul#product-grid') ||
        doc.getElementById('product-grid') ||
        doc.querySelector('ul.grid.product-grid')
      );
    };

    return fetch(fetchUrl, {
      method: 'GET',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => {
        const doc = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : null;
        if (!doc?.querySelector) return false;

        const remoteGrid = pickRemoteGrid(doc);
        const markup = (remoteGrid && remoteGrid.innerHTML && remoteGrid.innerHTML.trim()) || '';
        if (!markup) {
          console.warn('[skipp-collection-infinite-scroll] No product markup on page', pageNum, fetchUrl);
          return false;
        }

        this.gridContainer.insertAdjacentHTML('beforeend', markup);
        return true;
      })
      .catch((err) => {
        console.warn('[skipp-collection-infinite-scroll] Fetch failed', err, fetchUrl);
        return false;
      });
  }

  finish() {
    try {
      this.observer?.disconnect();
    } catch (_e3) {}

    this.classList.add('skipp-inf-scroll--done');

    const title = this.dataset.endText || 'You have reached the end';
    const sub = this.dataset.endSubtext || '';

    const tpl = document.getElementById('skipp-plp-end-cute-tpl');
    if (tpl?.content) {
      const fragment = tpl.content.cloneNode(true);
      const titleEl = fragment.querySelector('.skipp-plp-end-of-list__title');
      const subEl = fragment.querySelector('.skipp-plp-end-of-list__sub');
      if (titleEl) titleEl.textContent = title;
      if (subEl) {
        if (sub.trim()) subEl.textContent = sub;
        else subEl.remove();
      }

      let suggested = [];
      const payloadEl = document.getElementById('SkippSuggestedCollectionsPayload');
      if (payloadEl?.textContent?.trim()) {
        try {
          const parsed = JSON.parse(payloadEl.textContent.trim());
          if (Array.isArray(parsed)) suggested = parsed;
        } catch (_e) {
          /* ignore */
        }
      }

      const nav = fragment.querySelector('.skipp-plp-end-suggestions');
      const listEl = fragment.querySelector('.skipp-plp-end-suggestions__list');
      const suggestLabelEl = fragment.querySelector('.skipp-plp-end-suggestions__label');
      if (nav && listEl && suggested.length > 0) {
        const labelText = this.dataset.suggestionsLabel || '';
        if (suggestLabelEl) {
          if (labelText.trim()) suggestLabelEl.textContent = labelText;
          else suggestLabelEl.remove();
        }
        const maxLinks = 8;
        for (let i = 0; i < suggested.length && i < maxLinks; i++) {
          const row = suggested[i];
          if (!row || typeof row.title !== 'string' || typeof row.url !== 'string') continue;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = row.url;
          a.textContent = row.title;
          li.appendChild(a);
          listEl.appendChild(li);
        }
        if (listEl.children.length > 0) {
          nav.removeAttribute('hidden');
          nav.setAttribute('aria-label', labelText.trim() || 'Suggested collections');
        } else {
          nav.remove();
        }
      } else if (nav) {
        nav.remove();
      }

      while (this.firstChild) {
        this.removeChild(this.firstChild);
      }
      this.appendChild(fragment);
      return;
    }

    const wrap = document.createElement('div');
    wrap.className = 'skipp-plp-end-of-list';
    const p = document.createElement('p');
    p.className = 'cart__empty-text';
    p.textContent = title;
    wrap.appendChild(p);
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
    this.appendChild(wrap);
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', SkippCollectionInfiniteScroll);
}
